import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Sparkles, X, PenTool, Box, IndianRupee, ArrowRight } from 'lucide-react';

const STORAGE_KEY = 'acremitra_architect_launch_seen_v1';

export const ArchitectLaunchPopup = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Suppress on share-link + admin pages entirely.
    if (window.location.pathname.startsWith('/property/') ||
        window.location.pathname.startsWith('/admin') ||
        window.location.pathname.startsWith('/careers') ||
        window.location.pathname.startsWith('/hrm') ||
        window.location.pathname.startsWith('/site-intel')) return;
    const seen = window.localStorage.getItem(STORAGE_KEY);
    if (seen) return;
    // Suppress the AnnouncementPopup for this session — Architect launch takes priority.
    try {
      window.sessionStorage.setItem('acremitra_announcement_dismissed', '1');
    } catch (err) {
      console.warn('[ArchitectLaunchPopup] sessionStorage unavailable:', err);
    }
    const t = setTimeout(() => {
      // Bail out if a Radix Dialog is already open — its focus-trap would
      // eat clicks on our close button.
      if (document.querySelector('[role="dialog"][data-state="open"]')) return;
      // Never fire when the user has scrolled to the Career form — the
      // popup's z-index blocks the role dropdown. Same for any live form
      // that would frustrate the user.
      if (document.querySelector('[data-testid="career-role"], [data-testid="site-intel-scan"], [data-testid="add-property-form"]')) return;
      setOpen(true);
    }, 4000);   // give the page 4 s to settle so scroll-into-view finishes
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, '1');
    } catch (err) {
      console.warn('[ArchitectLaunchPopup] localStorage write failed:', err);
    }
    setOpen(false);
  };

  const explore = () => {
    close();
    navigate('/ai-architect');
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[1100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn"
      onClick={close}
      data-testid="architect-launch-popup"
    >
      <div
        className="bg-white rounded-3xl max-w-lg w-full overflow-hidden shadow-2xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={close}
          className="absolute top-3 right-3 text-white/80 hover:text-white z-10"
          data-testid="architect-launch-close"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        <div className="bg-gradient-to-br from-emerald-900 via-emerald-800 to-slate-900 px-7 pt-7 pb-10 text-white relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-[0.12] pointer-events-none"
            style={{
              backgroundImage:
                'linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)',
              backgroundSize: '24px 24px',
            }}
          />
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-400/20 border border-amber-300/40 rounded-full mb-4">
              <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-amber-200">
                Now Live · Acre Mitra
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold leading-tight mb-2">
              Introducing <span className="text-amber-300">Architect</span>
            </h2>
            <p className="text-emerald-50/85 text-sm leading-relaxed">
              Instant state-wise cost estimate for your dream home — and order a printed blueprint
              with a 3D home view, drafted by our senior architects.
            </p>
          </div>
        </div>

        <div className="px-7 py-6 space-y-3">
          <Feature icon={IndianRupee} title="Instant cost estimate" desc="State-calibrated 2026 rates, full BOM, no AI image fluff." />
          <Feature icon={Box} title="Modern Blueprint + 3D View" desc="CAD-rendered architectural drawings, production-ready." />
          <Feature icon={PenTool} title="Manual Blueprint + 3D View" desc="Hand-drawn by a draftsman, vastu-aware, beautifully detailed." />

          <button
            onClick={explore}
            className="mt-3 w-full bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 transition-colors"
            data-testid="architect-launch-cta"
          >
            Try Architect now <ArrowRight size={16} />
          </button>
          <button
            onClick={close}
            className="w-full text-xs text-slate-500 hover:text-slate-800 py-1"
            data-testid="architect-launch-dismiss"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const Feature = ({ icon: Icon, title, desc }) => (
  <div className="flex items-start gap-3">
    <div className="w-9 h-9 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center flex-shrink-0">
      <Icon size={16} className="text-emerald-700" />
    </div>
    <div>
      <div className="text-sm font-semibold text-slate-900">{title}</div>
      <div className="text-xs text-slate-500 leading-snug">{desc}</div>
    </div>
  </div>
);

export default ArchitectLaunchPopup;
